import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Search, Users, LayoutGrid, List, 
  ArrowUpDown, TrendingUp, TrendingDown, DollarSign, 
  BarChart3, Eye, ChevronRight, AlertTriangle,
  CheckCircle2, XCircle, Sparkles, 
  ArrowUp, ArrowDown, Building2, Crown, GitCompare, X, Moon,
  Activity, Target, Zap, Calendar, ChevronUp, ChevronDown as ChevronDownIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Count weekdays (Mon-Fri) from the 1st of current month UP TO but NOT including today.
// Used for "Avg A.C" calculation in Active Client metric.
const getWeekdaysMTD = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const today = now.getDate();
  let count = 0;
  for (let d = 1; d < today; d++) {
    const day = new Date(year, month, d).getDay();
    if (day !== 0 && day !== 6) count++; // exclude Sun (0) & Sat (6)
  }
  return Math.max(1, count); // avoid divide by zero
};

// Alert status configuration
const alertConfig = {
  excellent: { 
    label: 'Excellent', 
    color: '#10B981', 
    gradient: 'from-[#10B981] to-[#059669]',
    cardBg: 'from-[#10B981]/8 via-[#10B981]/3 to-transparent',
    icon: Crown,
    description: 'Top Performer'
  },
  good: { 
    label: 'Good', 
    color: '#3B82F6', 
    gradient: 'from-[#3B82F6] to-[#2563EB]',
    cardBg: 'from-[#3B82F6]/8 via-[#3B82F6]/3 to-transparent',
    icon: CheckCircle2,
    description: 'On Track'
  },
  warning: { 
    label: 'Warning', 
    color: '#F59E0B', 
    gradient: 'from-[#F59E0B] to-[#D97706]',
    cardBg: 'from-[#F59E0B]/8 via-[#F59E0B]/3 to-transparent',
    icon: AlertTriangle,
    description: 'Needs Attention'
  },
  critical: { 
    label: 'Critical', 
    color: '#EF4444', 
    gradient: 'from-[#EF4444] to-[#DC2626]',
    cardBg: 'from-[#EF4444]/8 via-[#EF4444]/3 to-transparent',
    icon: XCircle,
    description: 'Action Required'
  },
  inactive: { 
    label: 'Inactive', 
    color: '#6B7280', 
    gradient: 'from-[#6B7280] to-[#4B5563]',
    cardBg: 'from-[#6B7280]/8 via-[#6B7280]/3 to-transparent',
    icon: Moon,
    description: 'Not Active'
  }
};

// Health Score Detail Modal Component
const HealthScoreDetailModal = ({ open, onClose, ib }) => {
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
  
  if (!ib) return null;
  
  const isActive = ib.status === 'active';
  const alertStatus = getAlertStatus(ib.health_score, ib.monthly_growth || 0, ib.status);
  const config = alertConfig[alertStatus];
  
  // Use REAL data from IB - no mock data
  const currentScore = ib.health_score || 0;
  
  // Calculate score breakdown based on REAL metrics
  const commissionScore = Math.min(30, Math.round((ib.total_commission || 0) / 3500));
  const clientScore = Math.min(25, Math.round((ib.total_active_client || 0) / 4));
  const volumeScore = Math.min(25, Math.round((ib.total_traded_lot || 0) / 50));
  const depositScore = Math.min(20, Math.round((ib.total_deposit || 0) / 2500));
  const calculatedTotal = commissionScore + clientScore + volumeScore + depositScore;
  // Score breakdown components - using REAL calculated values
  const scoreBreakdown = [
    { label: 'Commission Performance', value: commissionScore, max: 30, color: '#10B981', icon: DollarSign },
    { label: 'Active Clients', value: clientScore, max: 25, color: '#8B5CF6', icon: Users },
    { label: 'Trading Volume', value: volumeScore, max: 25, color: '#F97316', icon: Activity },
    { label: 'Deposit Growth', value: depositScore, max: 20, color: '#3B82F6', icon: TrendingUp },
  ];
  
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1A1A22] border border-[#2A2A35] rounded-lg p-3 shadow-xl">
          <p className="text-[#6B7280] text-xs mb-1">{payload[0].payload.date}</p>
          <p className="text-white font-bold text-lg">{payload[0].value}</p>
          <p className="text-[#6B7280] text-[10px]">Health Score</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-gradient-to-br from-[#0D0D12] via-[#12121A] to-[#0D0D12] border border-[#2A2A35] text-white max-h-[90vh] overflow-y-auto p-0">
        {/* Premium Header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/20 via-transparent to-[#8B5CF6]/20" />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#E63946] to-transparent" />
          
          <div className="relative p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl border-2 border-[#2A2A35] bg-gradient-to-br from-[#252530] to-[#1A1A22] flex items-center justify-center overflow-hidden">
                  {ib.brand_logo_url ? (
                    <img src={ib.brand_logo_url} alt={ib.name} className="w-full h-full object-cover" />
                  ) : (
                    <Building2 size={28} className="text-[#4A4A5A]" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{ib.name}</h2>
                  <p className="text-sm text-[#6B7280]">Health Score Analysis</p>
                </div>
              </div>
              
              {/* Large Health Score Badge */}
              <div className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-2xl`}>
                <div className="absolute inset-0 rounded-2xl bg-black/20" />
                <span className="relative text-3xl font-black text-white">{isActive ? currentScore : 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-[#1A1A22] to-[#12121A] rounded-xl p-4 border border-[#2A2A35]">
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Current</p>
              <p className="text-2xl font-bold text-white">{currentScore}</p>
            </div>
            <div className="bg-gradient-to-br from-[#1A1A22] to-[#12121A] rounded-xl p-4 border border-[#2A2A35]">
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">vs Last Month</p>
              <div className="flex items-center gap-2">
                {ib.last_month_commission > 0 ? (
                  <>
                    <p className={`text-2xl font-bold ${(ib.monthly_growth || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      {(ib.monthly_growth || 0) >= 0 ? '+' : ''}{parseFloat(ib.monthly_growth || 0).toFixed(1)}%
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-[#6B7280]">N/A</p>
                )}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1A1A22] to-[#12121A] rounded-xl p-4 border border-[#2A2A35]">
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Commission MTD</p>
              <p className="text-lg font-bold text-[#10B981]">RM {(ib.total_commission || 0).toLocaleString()}</p>
            </div>
            <div className="bg-gradient-to-br from-[#1A1A22] to-[#12121A] rounded-xl p-4 border border-[#2A2A35]">
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Target Progress</p>
              {ib.target_commission > 0 ? (
                <p className={`text-2xl font-bold ${(ib.total_commission / ib.target_commission * 100) >= 100 ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                  {Math.round((ib.total_commission || 0) / ib.target_commission * 100)}%
                </p>
              ) : (
                <p className="text-lg font-bold text-[#6B7280]">No target</p>
              )}
            </div>
          </div>

          {/* Chart Section */}
          <div className="bg-gradient-to-br from-[#1A1A22] to-[#12121A] rounded-2xl border border-[#2A2A35] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#2A2A35]">
              <div className="flex items-center gap-2">
                <Activity size={18} className="text-[#E63946]" />
                <span className="font-semibold text-white">Score Trend</span>
              </div>
              <div className="flex bg-[#0D0D12] rounded-lg p-1">
                <button 
                  onClick={() => setViewMode('daily')}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'daily' ? 'bg-gradient-to-r from-[#E63946] to-[#D62839] text-white' : 'text-[#6B7280] hover:text-white'}`}
                >
                  Daily
                </button>
                <button 
                  onClick={() => setViewMode('weekly')}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'weekly' ? 'bg-gradient-to-r from-[#E63946] to-[#D62839] text-white' : 'text-[#6B7280] hover:text-white'}`}
                >
                  Weekly
                </button>
              </div>
            </div>
            
            <div className="p-4 h-64 flex items-center justify-center">
              <div className="text-center">
                <Activity size={32} className="text-[#4A4A5A] mx-auto mb-3" />
                <p className="text-[#6B7280] font-medium">Historical Trend Data Required</p>
                <p className="text-[10px] text-[#4A4A5A] mt-1">Chart will display once historical data is available</p>
              </div>
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 p-3 border-t border-[#2A2A35] text-[10px]">
              <div className="flex items-center gap-2">
                <div className="w-3 h-0.5 bg-[#10B981]" style={{ borderStyle: 'dashed' }} />
                <span className="text-[#6B7280]">Good (70+)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-0.5 bg-[#F59E0B]" style={{ borderStyle: 'dashed' }} />
                <span className="text-[#6B7280]">Warning (50)</span>
              </div>
            </div>
          </div>

          {/* Score Breakdown */}
          <div className="bg-gradient-to-br from-[#1A1A22] to-[#12121A] rounded-2xl border border-[#2A2A35] p-4">
            <div className="flex items-center gap-2 mb-4">
              <Target size={18} className="text-[#8B5CF6]" />
              <span className="font-semibold text-white">Score Breakdown</span>
            </div>
            
            <div className="space-y-4">
              {scoreBreakdown.map((item, idx) => {
                const Icon = item.icon;
                const percentage = (item.value / item.max) * 100;
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
                          <Icon size={16} style={{ color: item.color }} />
                        </div>
                        <span className="text-sm text-[#9CA3AF]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-white">{item.value}/{item.max}</span>
                    </div>
                    <div className="h-2 bg-[#0D0D12] rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, ${item.color}, ${item.color}80)`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 pt-4 border-t border-[#2A2A35] flex items-center justify-between">
              <span className="text-[#6B7280]">Total Score</span>
              <span className="text-xl font-bold text-white">{scoreBreakdown.reduce((a, b) => a + b.value, 0)}/100</span>
            </div>
          </div>

          {/* Performance Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-[#10B981]/10 to-transparent rounded-xl p-4 border border-[#10B981]/20">
              <DollarSign size={20} className="text-[#10B981] mb-2" />
              <p className="text-xs text-[#6B7280]">Commission MTD</p>
              <p className="text-lg font-bold text-white">RM {((ib.total_commission || 0)).toLocaleString()}</p>
            </div>
            <div className="bg-gradient-to-br from-[#8B5CF6]/10 to-transparent rounded-xl p-4 border border-[#8B5CF6]/20">
              <Users size={20} className="text-[#8B5CF6] mb-2" />
              <p className="text-xs text-[#6B7280]">Active Clients</p>
              <p className="text-lg font-bold text-white">{ib.total_active_client || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-[#F59E0B]/10 to-transparent rounded-xl p-4 border border-[#F59E0B]/20">
              <Zap size={20} className="text-[#F59E0B] mb-2" />
              <p className="text-xs text-[#6B7280]">Conversion</p>
              <p className="text-lg font-bold text-white">{ib.total_leads > 0 ? ((ib.total_closed / ib.total_leads) * 100).toFixed(0) : 0}%</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const getAlertStatus = (healthScore, monthlyGrowth, ibStatus) => {
  // If IB is inactive, return 'inactive' status (no health score alert)
  if (ibStatus === 'inactive') return 'inactive';
  
  if (healthScore >= 85 && monthlyGrowth >= 5) return 'excellent';
  if (healthScore >= 70) return 'good';
  if (healthScore >= 50) return 'warning';
  return 'critical';
};

// Premium IB Card - Grid View
const IBCardGrid = ({ ib, onClick, isCompareMode, isSelected, onToggleSelect, onHealthScoreClick }) => {
  const isActive = ib.status === 'active';
  const alertStatus = getAlertStatus(ib.health_score, ib.monthly_growth || 0, ib.status);
  const config = alertConfig[alertStatus];
  const AlertIcon = config.icon;
  const growth = ib.monthly_growth || 0;
  const displayHealthScore = isActive ? (ib.health_score || 0) : 'N/A';

  return (
    <div className="group cursor-pointer relative" onClick={isCompareMode ? () => onToggleSelect(ib) : onClick}>
      {/* Selection checkbox overlay for compare mode */}
      {isCompareMode && (
        <div className={`absolute top-3 right-3 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
          isSelected 
            ? 'bg-[#8B5CF6] border-[#8B5CF6]' 
            : 'bg-[#1A1A22]/80 border-[#3A3A45] hover:border-[#8B5CF6]'
        }`}>
          {isSelected && <CheckCircle2 size={14} className="text-white" />}
        </div>
      )}
      
      <div className={`relative bg-gradient-to-br ${config.cardBg} border-2 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-black/20 ${
        isSelected ? 'border-[#8B5CF6] shadow-lg shadow-[#8B5CF6]/20' : 'border-[#2A2A35] hover:border-[#3A3A45]'
      }`}>
        
        <div className="absolute inset-[1px] bg-gradient-to-br from-[#1A1A22] via-[#151519] to-[#121216] rounded-2xl" />
        <div className={`relative h-1 bg-gradient-to-r ${config.gradient}`} />
        
        <div className="relative px-4 py-2.5 border-b border-[#252530] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
              <AlertIcon size={12} className="text-white" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: config.color }}>
              {isActive ? config.label : 'Inactive'}
            </span>
          </div>
          <Badge className={`text-[8px] px-2 py-0.5 ${isActive 
            ? 'bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30' 
            : 'bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/30'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? 'bg-[#10B981] animate-pulse' : 'bg-[#EF4444]'}`} />
            {isActive ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        </div>

        <div className="relative p-4">
          {/* IB Profile Row with Health Score on right */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl border-2 border-[#2A2A35] bg-gradient-to-br from-[#252530] to-[#1A1A22] flex items-center justify-center overflow-hidden flex-shrink-0">
              {ib.brand_logo_url ? (
                <img src={ib.brand_logo_url} alt={ib.name} className="w-full h-full object-cover" />
              ) : (
                <Building2 size={22} className="text-[#4A4A5A]" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-[#F0F0F5] truncate group-hover:text-[#E63946] transition-colors">
                {ib.name}
              </h3>
              <p className="text-[10px] text-[#6B7280] mt-0.5 truncate">{ib.target_market || 'Introducing Broker'}</p>
            </div>
            
          {/* Growth badge + Health Score */}
          <div className="flex items-center gap-2">
            {/* Growth Badge */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${growth >= 0 ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>
              {growth >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {growth >= 0 ? '+' : ''}{parseFloat(growth).toFixed(1)}%
            </div>
            
            {/* Health Score - CLICKABLE for detail modal */}
            <button 
              onClick={(e) => { e.stopPropagation(); onHealthScoreClick && onHealthScoreClick(ib); }}
              className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-base font-bold text-white shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-200 cursor-pointer`}
              data-testid={`health-score-${ib.id}`}
              title="Click to view Health Score details"
            >
              {displayHealthScore}
            </button>
          </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mb-3">
            <MetricCard label="Commission" value={`RM ${(ib.total_commission || 0).toLocaleString()}`} extra={`avg : RM ${Math.round((ib.total_commission || 0) / getWeekdaysMTD()).toLocaleString()}`} color="#10B981" icon={DollarSign} target={ib.target_commission} current={ib.total_commission || 0} />
            <MetricCard label="Traded Lot" value={(ib.total_traded_lot || 0).toLocaleString()} extra={`avg : ${((ib.total_traded_lot || 0) / getWeekdaysMTD()).toFixed(1)}`} color="#F97316" icon={BarChart3} target={ib.target_tradedLot} current={ib.total_traded_lot || 0} />
            <MetricCard label="Active Client" value={ib.total_active_client || 0} extra={`avg : ${Math.round((ib.total_active_client || 0) / getWeekdaysMTD())}`} color="#8B5CF6" icon={Users} target={ib.target_activeClient} current={ib.total_active_client || 0} />
            <MetricCard label="Deposit" value={`$${(ib.total_deposit || 0).toLocaleString()}`} extra={`avg : $${Math.round((ib.total_deposit || 0) / getWeekdaysMTD()).toLocaleString()}`} color="#E63946" icon={DollarSign} target={ib.target_deposit} current={ib.total_deposit || 0} />
          </div>

          <div className="flex items-center justify-between py-2 px-3 bg-[#1E1E26] rounded-xl border border-[#2A2A35] mb-3 text-[10px]">
            <div className="text-center flex-1">
              <p className="text-[#6B7280] uppercase tracking-wider">Subscribers</p>
              <p className="font-bold text-[#3B82F6]">{(ib.total_subs || 0).toLocaleString()}</p>
            </div>
            <div className="w-px h-6 bg-[#2A2A35]" />
            <div className="text-center flex-1">
              <p className="text-[#6B7280] uppercase tracking-wider">Leads</p>
              <p className="font-bold text-[#F59E0B]">{ib.total_leads || 0}</p>
            </div>
            <div className="w-px h-6 bg-[#2A2A35]" />
            <div className="text-center flex-1">
              <p className="text-[#6B7280] uppercase tracking-wider">Closed</p>
              <p className="font-bold text-[#10B981]">{ib.total_closed || 0}</p>
            </div>
            <div className="w-px h-6 bg-[#2A2A35]" />
            <div className="text-center flex-1">
              <p className="text-[#6B7280] uppercase tracking-wider">Com/Lot</p>
              <p className={`font-bold ${(ib.com_per_lot || 0) >= 12 ? 'text-[#10B981]' : (ib.com_per_lot || 0) >= 8 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`} data-testid={`com-per-lot-${ib.id}`}>{(ib.com_per_lot || 0) > 0 ? `$${ib.com_per_lot.toFixed(1)}` : '-'}</p>
            </div>
          </div>
          
          {!isCompareMode && (
            <button className="w-full py-2.5 px-4 rounded-xl bg-[#1E1E26] border border-[#2A2A35] text-xs font-semibold text-[#9CA3AF] hover:text-white hover:border-[#E63946]/40 hover:bg-[#E63946]/10 transition-all flex items-center justify-center gap-2 group/btn">
              <Eye size={13} />
              <span>View Details</span>
              <ChevronRight size={13} className="opacity-0 -ml-1 group-hover/btn:opacity-100 group-hover/btn:ml-0 transition-all" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Metric Card Component with optional target progress
const MetricCard = ({ label, value, color, icon: Icon, target, current, extra }) => {
  const pct = target > 0 ? Math.min(Math.round((current / target) * 100), 999) : null;
  return (
    <div className="bg-[#1E1E26] rounded-xl p-2.5 border border-[#2A2A35] hover:border-[#3A3A45] transition-colors overflow-hidden">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={11} style={{ color }} className="flex-shrink-0" />
        <span className="text-[8px] uppercase tracking-wider text-[#6B7280] font-semibold truncate">{label}</span>
        {pct !== null && <span className={`text-[8px] font-bold ml-auto ${pct >= 100 ? 'text-[#10B981]' : pct >= 50 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>{pct}%</span>}
      </div>
      <span className="text-xs sm:text-sm font-bold text-[#E0E0E5] truncate block">{value}</span>
      {extra && <span className="text-[9px] font-semibold text-[#9CA3AF] block mt-0.5" data-testid="metric-extra">({extra})</span>}
      {pct !== null && (
        <div className="mt-1.5 h-1 bg-[#2A2A35] rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-[#10B981]' : pct >= 50 ? 'bg-[#F59E0B]' : 'bg-[#EF4444]'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      )}
    </div>
  );
};

// Table Row Component
const IBTableRow = ({ ib, onClick, isCompareMode, isSelected, onToggleSelect }) => {
  const isActive = ib.status === 'active';
  const alertStatus = getAlertStatus(ib.health_score, ib.monthly_growth || 0, ib.status);
  const config = alertConfig[alertStatus];
  const AlertIcon = config.icon;
  const growth = ib.monthly_growth || 0;
  const displayHealthScore = isActive ? (ib.health_score || 0) : 'N/A';

  return (
    <tr 
      className={`border-b border-[#252530] hover:bg-[#1A1A22] transition-colors cursor-pointer group ${isSelected ? 'bg-[#8B5CF6]/10' : ''}`} 
      onClick={isCompareMode ? () => onToggleSelect(ib) : onClick}
    >
      {isCompareMode && (
        <td className="py-3.5 px-3">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
            isSelected ? 'bg-[#8B5CF6] border-[#8B5CF6]' : 'border-[#3A3A45]'
          }`}>
            {isSelected && <CheckCircle2 size={12} className="text-white" />}
          </div>
        </td>
      )}
      <td className="py-3.5 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl border-2 border-[#2A2A35] bg-[#1E1E26] flex items-center justify-center overflow-hidden">
            {ib.brand_logo_url ? (
              <img src={ib.brand_logo_url} alt={ib.name} className="w-full h-full object-cover" />
            ) : (
              <Building2 size={18} className="text-[#4A4A5A]" />
            )}
          </div>
          <div>
            <p className="font-bold text-[#F0F0F5] text-sm group-hover:text-[#E63946] transition-colors">{ib.name}</p>
            <p className="text-[10px] text-[#6B7280]">{ib.target_market || 'IB'}</p>
          </div>
        </div>
      </td>
      <td className="py-3.5 px-3 text-center">
        <div className={`inline-flex w-9 h-9 rounded-xl bg-gradient-to-br ${config.gradient} items-center justify-center text-sm font-bold text-white shadow-lg`}>
          {displayHealthScore}
        </div>
      </td>
      <td className="py-3.5 px-3">
        <div className="flex items-center gap-1.5">
          <div className={`w-5 h-5 rounded-md bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <AlertIcon size={10} className="text-white" />
          </div>
          <span className="text-[10px] font-bold" style={{ color: config.color }}>{isActive ? config.label : 'Inactive'}</span>
        </div>
      </td>
      <td className="py-3.5 px-3">
        <Badge className={`text-[9px] ${isActive 
          ? 'bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30' 
          : 'bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/30'
        }`}>
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      </td>
      <td className="py-3.5 px-3 text-right">
        <p className="font-bold text-[#10B981] text-sm">RM {(ib.total_commission || 0).toLocaleString()} <span className="text-[10px] font-semibold text-[#9CA3AF]">(avg : RM {Math.round((ib.total_commission || 0) / getWeekdaysMTD()).toLocaleString()})</span></p>
      </td>
      <td className="py-3.5 px-3 text-right">
        <p className="font-bold text-[#E0E0E5] text-sm">{(ib.total_traded_lot || 0).toLocaleString()} <span className="text-[10px] font-semibold text-[#9CA3AF]">(avg : {((ib.total_traded_lot || 0) / getWeekdaysMTD()).toFixed(1)})</span></p>
      </td>
      <td className="py-3.5 px-3 text-right">
        <p className="font-bold text-[#E0E0E5] text-sm">{ib.total_active_client || 0} <span className="text-[10px] font-semibold text-[#9CA3AF]">(avg : {Math.round((ib.total_active_client || 0) / getWeekdaysMTD())})</span></p>
      </td>
      <td className="py-3.5 px-3 text-right">
        <div className={`inline-flex items-center gap-0.5 text-sm font-bold ${growth >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
          {growth >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          {Math.abs(growth).toFixed(1)}%
        </div>
      </td>
      {!isCompareMode && (
        <td className="py-3.5 px-3">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#E63946] hover:bg-[#E63946]/10">
            <Eye size={14} />
          </Button>
        </td>
      )}
    </tr>
  );
};

// Comparison Panel Component
const ComparisonPanel = ({ selectedIBs, onClose, onRemoveIB }) => {
  if (selectedIBs.length === 0) return null;

  const metrics = [
    { key: 'health_score', label: 'Health Score', format: (v, ib) => ib.status === 'active' ? (v || 0) : 'N/A', color: '#8B5CF6' },
    { key: 'total_commission', label: 'Commission', format: (v) => `RM ${(v || 0).toLocaleString()}`, color: '#10B981' },
    { key: 'total_traded_lot', label: 'Traded Lot', format: (v) => (v || 0).toLocaleString(), color: '#F97316' },
    { key: 'com_per_lot', label: 'Com/Lot', format: (v) => (v || 0) > 0 ? `$${v.toFixed(2)}` : '-', color: '#06B6D4' },
    { key: 'total_active_client', label: 'Active Client', format: (v) => v || 0, color: '#3B82F6' },
    { key: 'monthly_growth', label: 'Growth Rate', format: (v) => `${(v || 0) >= 0 ? '+' : ''}${(v || 0).toFixed(1)}%`, color: '#10B981' },
  ];

  const maxValues = metrics.reduce((acc, m) => {
    const values = selectedIBs.filter(ib => ib.status === 'active').map(ib => ib[m.key] || 0);
    acc[m.key] = values.length > 0 ? Math.max(...values) : 0;
    return acc;
  }, {});

  return (
    <div className="fixed bottom-0 left-64 right-0 bg-[#0F0F14] border-t border-[#252530] shadow-2xl shadow-black/50 z-40 max-h-[60vh] overflow-auto">
      {/* Header */}
      <div className="sticky top-0 px-6 py-4 border-b border-[#252530] bg-[#0F0F14] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center">
            <GitCompare size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">IB Comparison</h3>
            <p className="text-xs text-[#6B7280]">Comparing {selectedIBs.length} IBs side by side</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-[#6B7280] hover:text-white">
          <X size={18} />
        </Button>
      </div>

      {/* IB Headers */}
      <div className="px-6 py-4 border-b border-[#252530] bg-[#0A0A0D]">
        <div className="grid gap-4" style={{ gridTemplateColumns: `180px repeat(${selectedIBs.length}, 1fr)` }}>
          <div className="text-xs font-semibold text-[#6B7280] uppercase">Metric</div>
          {selectedIBs.map(ib => {
            const isActive = ib.status === 'active';
            const status = getAlertStatus(ib.health_score, ib.monthly_growth || 0, ib.status);
            const config = alertConfig[status];
            return (
              <div key={ib.id} className="text-center relative group">
                <button 
                  onClick={() => onRemoveIB(ib.id)}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#EF4444] text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <X size={12} />
                </button>
                <div className={`inline-flex w-12 h-12 rounded-xl bg-gradient-to-br ${config.gradient} items-center justify-center text-lg font-bold text-white mb-2`}>
                  {isActive ? (ib.health_score || 0) : 'N/A'}
                </div>
                <p className="text-sm font-bold text-white">{ib.name}</p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: config.color }}>{isActive ? config.label : 'Inactive'}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Metrics Comparison */}
      <div className="px-6 py-4 space-y-3">
        {metrics.map(metric => (
          <div key={metric.key} className="grid gap-4 items-center" style={{ gridTemplateColumns: `180px repeat(${selectedIBs.length}, 1fr)` }}>
            <div className="text-xs font-semibold text-[#6B7280]">{metric.label}</div>
            {selectedIBs.map(ib => {
              const value = ib[metric.key] || 0;
              const maxVal = maxValues[metric.key];
              const percentage = maxVal > 0 ? (value / maxVal) * 100 : 0;
              const isMax = value === maxVal && maxVal > 0 && ib.status === 'active';
              const isInactive = ib.status === 'inactive';
              
              return (
                <div key={ib.id} className="bg-[#1A1A22] rounded-xl p-3 border border-[#252530]">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-bold ${isMax ? 'text-white' : 'text-[#9CA3AF]'}`}>
                      {metric.format(value, ib)}
                    </span>
                    {isMax && !isInactive && (
                      <Badge className="text-[8px] bg-[#10B981]/20 text-[#10B981] border-0 px-1.5">
                        <Sparkles size={8} className="mr-0.5" /> Best
                      </Badge>
                    )}
                  </div>
                  <div className="h-1.5 bg-[#252530] rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: isInactive && metric.key === 'health_score' ? '0%' : `${percentage}%`,
                        backgroundColor: isMax ? metric.color : '#4A4A5A'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      <div className="px-6 py-4 border-t border-[#252530]">
        <p className="text-xs font-semibold text-[#6B7280] mb-4 uppercase">Commission Comparison</p>
        <div className="flex items-end justify-around h-28 gap-6">
          {selectedIBs.map(ib => {
            const isActive = ib.status === 'active';
            const status = getAlertStatus(ib.health_score, ib.monthly_growth || 0, ib.status);
            const config = alertConfig[status];
            const height = maxValues.total_commission > 0 
              ? ((ib.total_commission || 0) / maxValues.total_commission) * 100 
              : 0;
            return (
              <div key={ib.id} className="flex flex-col items-center gap-2 flex-1 max-w-[120px]">
                <span className="text-xs text-[#6B7280]">RM {(ib.total_commission || 0).toLocaleString()}</span>
                <div 
                  className={`w-full rounded-t-lg bg-gradient-to-t ${config.gradient} transition-all duration-500`}
                  style={{ height: `${Math.max(height, 5)}%` }}
                />
                <span className="text-[10px] text-white font-medium truncate w-full text-center">{ib.name}</span>
                {!isActive && <span className="text-[8px] text-[#EF4444]">(Inactive)</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Status Filter Dialog
const StatusFilterDialog = ({ open, onClose, status, ibs, onSelectIB }) => {
  if (!status || !alertConfig[status]) return null;
  const config = alertConfig[status];
  const AlertIcon = config?.icon || XCircle;
  
  // For inactive status, filter by ib.status, otherwise filter by getAlertStatus
  const filteredIBs = status === 'inactive' 
    ? ibs.filter(ib => ib.status === 'inactive')
    : ibs.filter(ib => 
        ib.status === 'active' && getAlertStatus(ib.health_score, ib.monthly_growth || 0, ib.status) === status
      );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#151519] border-[#2A2A35] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-white">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}>
              <AlertIcon size={20} className="text-white" />
            </div>
            <div>
              <span style={{ color: config.color }}>{config.label}</span> IBs
              <p className="text-xs text-[#6B7280] font-normal mt-0.5">{filteredIBs.length} IB dalam kategori ini</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-3 space-y-2 max-h-[350px] overflow-y-auto">
          {filteredIBs.length === 0 ? (
            <div className="text-center py-8 text-[#6B7280]">
              <AlertIcon size={28} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Tiada IB dalam kategori ini</p>
            </div>
          ) : (
            filteredIBs.map(ib => (
              <div 
                key={ib.id}
                onClick={() => { onSelectIB(ib); onClose(); }}
                className="flex items-center justify-between p-3 rounded-xl bg-[#1A1A22] border border-[#252530] hover:border-[#E63946]/40 cursor-pointer transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#1E1E26] border border-[#2A2A35] flex items-center justify-center overflow-hidden">
                    {ib.brand_logo_url ? (
                      <img src={ib.brand_logo_url} alt={ib.name} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 size={16} className="text-[#4A4A5A]" />
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-[#F0F0F5] text-sm group-hover:text-[#E63946] transition-colors">{ib.name}</p>
                    <p className="text-[10px] text-[#6B7280]">
                      Health: {ib.status === 'active' ? ib.health_score : 'N/A'} • Growth: {(ib.monthly_growth || 0).toFixed(1)}%
                    </p>
                  </div>
                </div>
                <ChevronRight size={14} className="text-[#4A4A5A] group-hover:text-[#E63946]" />
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Alert Status Badge
const AlertStatusBadge = ({ status, count, onClick }) => {
  const config = alertConfig[status];
  const Icon = config.icon;
  
  return (
    <button 
      onClick={onClick}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-[#1A1A22] border border-[#252530] hover:border-[#3A3A45] transition-all"
      data-testid={`alert-${status}`}
    >
      <div className={`w-5 h-5 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
        <Icon size={10} className="text-white" />
      </div>
      <span className="text-sm font-bold" style={{ color: config.color }}>{count}</span>
    </button>
  );
};

export default function ListOfIBPage() {
  const [ibs, setIbs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // Default to active IBs only
  const [sortBy, setSortBy] = useState('health'); // Default sort by Health Score
  const [viewMode, setViewMode] = useState('grid');
  const [statusDialog, setStatusDialog] = useState({ open: false, status: null });
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [healthScoreModal, setHealthScoreModal] = useState({ open: false, ib: null });
  const navigate = useNavigate();

  useEffect(() => {
    fetchIBs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIBs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Get IB list
      const response = await axios.get(`${API}/ib/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get MTD date range (start of current month to today)
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const dateFrom = `${startOfMonth.getFullYear()}-${String(startOfMonth.getMonth() + 1).padStart(2, '0')}-01`;
      const dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      // Get performance data for all IBs (MTD)
      const perfResponse = await axios.get(`${API}/performance/overall`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: dateFrom, date_to: dateTo }
      });
      
      const ibPerformance = perfResponse.data.ib_performance || [];
      
      // Fetch monthly targets for ALL channels in batch (faster than per-IB)
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      let allTargets = {};
      try {
        const targetPromises = response.data.map(ib => 
          axios.get(`${API}/channel-targets/${ib.id}/${currentYear}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: {} }))
        );
        const targetResults = await Promise.all(targetPromises);
        const monthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
        response.data.forEach((ib, i) => {
          allTargets[ib.id] = targetResults[i].data?.monthly?.[monthKey] || {};
        });
      } catch {}
      
      // Also fetch last month SAME PERIOD for fair growth comparison
      // e.g., if today is Mar 11, compare Mar 1-11 vs Feb 1-11
      const dayOfMonth = now.getDate();
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthSameDay = new Date(now.getFullYear(), now.getMonth() - 1, Math.min(dayOfMonth, new Date(now.getFullYear(), now.getMonth(), 0).getDate()));
      const lastDateFrom = `${lastMonthStart.getFullYear()}-${String(lastMonthStart.getMonth() + 1).padStart(2, '0')}-01`;
      const lastDateTo = `${lastMonthSameDay.getFullYear()}-${String(lastMonthSameDay.getMonth() + 1).padStart(2, '0')}-${String(lastMonthSameDay.getDate()).padStart(2, '0')}`;
      
      let lastMonthPerf = [];
      try {
        const lastPerfRes = await axios.get(`${API}/performance/overall`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { date_from: lastDateFrom, date_to: lastDateTo }
        });
        lastMonthPerf = lastPerfRes.data.ib_performance || [];
      } catch {}
      
      // Enrich IB data with real performance metrics
      const enrichedData = response.data.map(ib => {
        const perf = ibPerformance.find(p => p.ib_id === ib.id) || {};
        
        // Use ib/list data as primary (correctly filtered by enabled brokers)
        const commission = ib.total_commission || perf.commission || 0;
        const deposit = ib.total_deposit || perf.deposit || 0;
        const activeClient = ib.total_active_client || perf.active_client || 0;
        const tradedLot = ib.total_traded_lot || perf.traded_lot || 0;
        const leads = ib.total_leads || perf.leads || 0;
        const closed = ib.total_closed || perf.closed || 0;
        const subs = ib.total_subs || perf.total_subs || 0;
        const hasData = commission > 0 || activeClient > 0 || tradedLot > 0 || deposit > 0;
        
        // Health score from REAL data (all months combined)
        let healthScore = 0;
        if (hasData && ib.status === 'active') {
          // Commission score (0-30)
          if (commission > 100000) healthScore += 30;
          else if (commission > 50000) healthScore += 25;
          else if (commission > 20000) healthScore += 20;
          else if (commission > 10000) healthScore += 15;
          else if (commission > 5000) healthScore += 10;
          else if (commission > 0) healthScore += 5;
          
          // Active clients score (0-25)
          if (activeClient > 1000) healthScore += 25;
          else if (activeClient > 500) healthScore += 20;
          else if (activeClient > 100) healthScore += 15;
          else if (activeClient > 50) healthScore += 10;
          else if (activeClient > 10) healthScore += 5;
          
          // Traded lot score (0-25)
          if (tradedLot > 2000) healthScore += 25;
          else if (tradedLot > 1000) healthScore += 20;
          else if (tradedLot > 500) healthScore += 15;
          else if (tradedLot > 100) healthScore += 10;
          else if (tradedLot > 0) healthScore += 5;
          
          // Deposit score (0-20)
          if (deposit > 50000) healthScore += 20;
          else if (deposit > 20000) healthScore += 15;
          else if (deposit > 10000) healthScore += 10;
          else if (deposit > 1000) healthScore += 5;
        }
        
        // Real month-vs-month growth (commission this month vs last month)
        const lastPerf = lastMonthPerf.find(p => p.ib_id === ib.id) || {};
        const lastCommission = lastPerf.commission || 0;
        const realGrowth = lastCommission > 0 ? (((commission - lastCommission) / lastCommission) * 100) : (commission > 0 ? 100 : 0);
        
        // Get targets for this IB
        const targets = allTargets[ib.id] || {};
        
        return {
          ...ib,
          health_score: ib.status === 'active' ? Math.min(healthScore, 100) : 0,
          total_commission: commission,
          total_deposit: deposit,
          total_active_client: activeClient,
          total_traded_lot: tradedLot,
          total_subs: subs,
          total_leads: leads,
          total_closed: closed,
          monthly_growth: realGrowth,
          last_month_commission: lastCommission,
          target_commission: parseFloat(targets.commission) || 0,
          target_tradedLot: parseFloat(targets.tradedLot) || 0,
          target_activeClient: parseFloat(targets.activeClient) || 0,
          target_subscribers: parseFloat(targets.subscribers) || 0,
          target_leads: parseFloat(targets.leads) || 0,
          target_closed: parseFloat(targets.closed) || 0,
          target_deposit: parseFloat(targets.deposit) || 0,
          data_count: perf.data_count || 0,
          target_market: ib.target_market || 'Malaysia'
        };
      });
      
      setIbs(enrichedData);
    } catch (error) {
      toast.error('Failed to load IBs');
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (ib) => {
    setSelectedForCompare(prev => {
      const isSelected = prev.find(i => i.id === ib.id);
      if (isSelected) {
        return prev.filter(i => i.id !== ib.id);
      }
      if (prev.length >= 4) {
        toast.warning('Maximum 4 IBs can be compared');
        return prev;
      }
      return [...prev, ib];
    });
  };

  const handleRemoveFromCompare = (ibId) => {
    setSelectedForCompare(prev => prev.filter(i => i.id !== ibId));
  };

  const toggleCompareMode = () => {
    if (isCompareMode) {
      setSelectedForCompare([]);
    }
    setIsCompareMode(!isCompareMode);
  };

  const filteredIBs = ibs
    .filter(ib => {
      const matchesSearch = ib.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filter === 'all' || ib.status === filter;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name': return a.name.localeCompare(b.name);
        case 'health': return (b.health_score || 0) - (a.health_score || 0);
        case 'commission': return (b.total_commission || 0) - (a.total_commission || 0);
        case 'clients': return (b.total_active_client || 0) - (a.total_active_client || 0);
        case 'growth': return (b.monthly_growth || 0) - (a.monthly_growth || 0);
        default: return 0;
      }
    });

  // Alert counts - only for ACTIVE IBs
  const activeIbs = ibs.filter(ib => ib.status === 'active');
  const alertCounts = {
    excellent: activeIbs.filter(ib => getAlertStatus(ib.health_score, ib.monthly_growth, ib.status) === 'excellent').length,
    good: activeIbs.filter(ib => getAlertStatus(ib.health_score, ib.monthly_growth, ib.status) === 'good').length,
    warning: activeIbs.filter(ib => getAlertStatus(ib.health_score, ib.monthly_growth, ib.status) === 'warning').length,
    critical: activeIbs.filter(ib => getAlertStatus(ib.health_score, ib.monthly_growth, ib.status) === 'critical').length,
  };
  const inactiveCount = ibs.filter(ib => ib.status === 'inactive').length;

  const handleIBClick = (ib) => {
    localStorage.setItem('selectedIB', ib.id);
    window.dispatchEvent(new Event('ibChanged'));
    navigate(`/ib/${ib.id}/details`);
  };

  if (loading) {
    return (
      <ResponsiveLayout>
        <div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]">
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 border-3 border-[hsl(var(--border))] rounded-full" />
              <div className="w-12 h-12 border-3 border-[hsl(var(--primary))] border-t-transparent rounded-full animate-spin absolute inset-0" />
            </div>
            <p className="text-[hsl(var(--text-muted))] text-sm">Loading IBs...</p>
          </div>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout>
      <div className={`flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] ${selectedForCompare.length > 0 ? 'pb-[400px]' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[hsl(var(--surface))]/95 backdrop-blur-xl border-b border-[hsl(var(--border))]">
          <div className="px-4 lg:px-6 py-3 lg:py-4">
            {/* Title Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] flex items-center justify-center shadow-lg">
                  <Users size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="text-lg lg:text-xl font-bold tracking-tight text-[hsl(var(--text-primary))]" data-testid="page-title">
                    Introducing Brokers
                  </h1>
                  <p className="text-xs text-[hsl(var(--text-muted))]">MTD Performance • Auto refresh from 1st of month</p>
                </div>
              </div>
              
              {/* Compare Button */}
              <Button
                onClick={toggleCompareMode}
                size="sm"
                className={`${isCompareMode 
                  ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                  : 'bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:border-purple-500'
                }`}
                data-testid="compare-btn"
              >
                <GitCompare size={14} className="mr-1.5" />
                {isCompareMode ? `Cancel (${selectedForCompare.length})` : 'Compare'}
              </Button>
            </div>

            {/* Compare Mode Instructions */}
            {isCompareMode && (
              <div className="mb-4 px-4 py-3 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GitCompare size={18} className="text-[#8B5CF6]" />
                  <p className="text-sm text-[#8B5CF6]">
                    <span className="font-semibold">Compare Mode:</span> Pilih 2-4 IB untuk compare. 
                    <span className="text-[#A78BFA]"> ({selectedForCompare.length}/4 selected)</span>
                  </p>
                </div>
                {selectedForCompare.length >= 2 && (
                  <Badge className="bg-[#8B5CF6] text-white">Comparison panel active below ↓</Badge>
                )}
              </div>
            )}

            {/* Controls Row */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[120px] max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4A4A5A]" size={14} />
                <Input type="text" placeholder="Search IBs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-[#151519] border-[#252530] focus:border-[#E63946]/50 text-white h-9 rounded-xl text-sm placeholder:text-[#4A4A5A]" data-testid="search-input" />
              </div>
              
              <div className="flex gap-0.5 p-0.5 bg-[#151519] rounded-xl border border-[#252530]">
                {['all', 'active', 'inactive'].map((f) => (
                  <Button key={f} variant="ghost" size="sm" onClick={() => setFilter(f)}
                    className={`px-3 h-8 rounded-lg text-xs font-semibold transition-all ${filter === f ? 'bg-gradient-to-r from-[#E63946] to-[#D62839] text-white shadow-lg' : 'text-[#6B7280] hover:text-white hover:bg-[#1E1E26]'}`}
                    data-testid={`filter-${f}`}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </div>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36 bg-[#151519] border-[#252530] text-white h-9 rounded-xl text-xs" data-testid="sort-select">
                  <ArrowUpDown size={12} className="mr-1.5 text-[#6B7280]" /><SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#151519] border-[#252530]">
                  <SelectItem value="name" className="text-white text-xs">Name</SelectItem>
                  <SelectItem value="health" className="text-white text-xs">Health Score</SelectItem>
                  <SelectItem value="commission" className="text-white text-xs">Commission</SelectItem>
                  <SelectItem value="clients" className="text-white text-xs">Active Client</SelectItem>
                  <SelectItem value="growth" className="text-white text-xs">Growth</SelectItem>
                </SelectContent>
              </Select>

              <AlertStatusBadge status="excellent" count={alertCounts.excellent} onClick={() => setStatusDialog({ open: true, status: 'excellent' })} />
              <AlertStatusBadge status="good" count={alertCounts.good} onClick={() => setStatusDialog({ open: true, status: 'good' })} />
              <AlertStatusBadge status="warning" count={alertCounts.warning} onClick={() => setStatusDialog({ open: true, status: 'warning' })} />
              <AlertStatusBadge status="critical" count={alertCounts.critical} onClick={() => setStatusDialog({ open: true, status: 'critical' })} />
              <AlertStatusBadge status="inactive" count={inactiveCount} onClick={() => setStatusDialog({ open: true, status: 'inactive' })} />

              <div className="flex p-0.5 bg-[#151519] rounded-xl border border-[#252530] ml-auto">
                <Button variant="ghost" size="sm" onClick={() => setViewMode('grid')}
                  className={`w-8 h-8 p-0 rounded-lg ${viewMode === 'grid' ? 'bg-gradient-to-r from-[#E63946] to-[#D62839] text-white' : 'text-[#6B7280] hover:text-white'}`} data-testid="view-grid">
                  <LayoutGrid size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setViewMode('table')}
                  className={`w-8 h-8 p-0 rounded-lg ${viewMode === 'table' ? 'bg-gradient-to-r from-[#E63946] to-[#D62839] text-white' : 'text-[#6B7280] hover:text-white'}`} data-testid="view-table">
                  <List size={14} />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-3 sm:p-6">
          {filteredIBs.length === 0 ? (
            <div className="text-center py-16 bg-[#151519] rounded-2xl border border-[#252530]">
              <div className="w-14 h-14 mx-auto rounded-xl bg-[#1E1E26] flex items-center justify-center mb-3">
                <Users size={24} className="text-[#4A4A5A]" />
              </div>
              <p className="text-[#9CA3AF] font-medium">No IBs found</p>
              <p className="text-xs text-[#6B7280] mt-1">Try adjusting your search or filter</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full" data-testid="ib-grid">
              {filteredIBs.map(ib => (
                <IBCardGrid 
                  key={ib.id} 
                  ib={ib} 
                  onClick={() => handleIBClick(ib)}
                  isCompareMode={isCompareMode}
                  isSelected={selectedForCompare.some(i => i.id === ib.id)}
                  onToggleSelect={handleToggleSelect}
                  onHealthScoreClick={(ib) => setHealthScoreModal({ open: true, ib })}
                />
              ))}
            </div>
          ) : (
            <div className="bg-[#151519] rounded-2xl border border-[#252530] overflow-hidden" data-testid="ib-table">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#252530] bg-[#121216]">
                    {isCompareMode && <th className="py-3 px-3 w-10"></th>}
                    <th className="text-left py-3 px-4 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">IB Profile</th>
                    <th className="text-center py-3 px-3 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">Health</th>
                    <th className="text-left py-3 px-3 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">Alert</th>
                    <th className="text-left py-3 px-3 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">Status</th>
                    <th className="text-right py-3 px-3 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">Commission</th>
                    <th className="text-right py-3 px-3 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">Lot</th>
                    <th className="text-right py-3 px-3 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">Active Client</th>
                    <th className="text-right py-3 px-3 text-[9px] font-bold text-[#6B7280] uppercase tracking-wider">Growth</th>
                    {!isCompareMode && <th className="py-3 px-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredIBs.map(ib => (
                    <IBTableRow 
                      key={ib.id} 
                      ib={ib} 
                      onClick={() => handleIBClick(ib)}
                      isCompareMode={isCompareMode}
                      isSelected={selectedForCompare.some(i => i.id === ib.id)}
                      onToggleSelect={handleToggleSelect}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 text-xs text-[#6B7280]">
            Showing {filteredIBs.length} of {ibs.length} IBs
          </div>
        </div>

        {/* Comparison Panel */}
        {selectedForCompare.length >= 2 && (
          <ComparisonPanel 
            selectedIBs={selectedForCompare}
            onClose={() => {
              setSelectedForCompare([]);
              setIsCompareMode(false);
            }}
            onRemoveIB={handleRemoveFromCompare}
          />
        )}

        {/* Status Filter Dialog */}
        <StatusFilterDialog 
          open={statusDialog.open} 
          onClose={() => setStatusDialog({ open: false, status: null })}
          status={statusDialog.status}
          ibs={ibs}
          onSelectIB={handleIBClick}
        />

        {/* Health Score Detail Modal */}
        <HealthScoreDetailModal
          open={healthScoreModal.open}
          onClose={() => setHealthScoreModal({ open: false, ib: null })}
          ib={healthScoreModal.ib}
        />
      </div>
    </ResponsiveLayout>
  );
}
